import type { PullPlanWBSNode, ScopeMember } from "../../../../models";

/** Ids of nodes with no children — the only checkable rows. */
export function leafIdSet(nodes: PullPlanWBSNode[]): Set<number> {
  const parents = new Set<number>();
  for (const n of nodes) {
    if (n.parent_id != null) parents.add(n.parent_id);
  }
  const leaves = new Set<number>();
  for (const n of nodes) {
    if (!parents.has(n.id)) leaves.add(n.id);
  }
  return leaves;
}

/** Depth-first visibility under collapsed branches: a node renders only when
 *  none of its ancestors are collapsed. */
export function visibleNodes(
  nodes: PullPlanWBSNode[],
  collapsed: Set<number>,
): PullPlanWBSNode[] {
  const out: PullPlanWBSNode[] = [];
  // Stack of collapsed ancestor depths; any node deeper than the top is hidden.
  const hiddenDepths: number[] = [];
  for (const n of nodes) {
    while (hiddenDepths.length > 0 && n.depth <= hiddenDepths[hiddenDepths.length - 1]) {
      hiddenDepths.pop();
    }
    if (hiddenDepths.length > 0) continue;
    out.push(n);
    if (collapsed.has(n.id)) hiddenDepths.push(n.depth);
  }
  return out;
}

/** What a checked leaf holds, summarized from scope members. */
export interface LeafFacts {
  total: number;
  untagged: number;
  /** Takt section → member count, largest first. */
  sections: Array<{ name: string; count: number }>;
}

export function leafFactsByWbs(members: ScopeMember[]): Map<number, LeafFacts> {
  const acc = new Map<number, { total: number; untagged: number; sections: Map<string, number> }>();
  for (const m of members) {
    let f = acc.get(m.wbs_node_id);
    if (!f) {
      f = { total: 0, untagged: 0, sections: new Map() };
      acc.set(m.wbs_node_id, f);
    }
    f.total += 1;
    const section = (m.takt_section_name || "").trim();
    if (section) f.sections.set(section, (f.sections.get(section) ?? 0) + 1);
    else f.untagged += 1;
  }
  const out = new Map<number, LeafFacts>();
  acc.forEach((f, wbsId) => {
    out.set(wbsId, {
      total: f.total,
      untagged: f.untagged,
      sections: Array.from(f.sections, ([name, count]) => ({ name, count })).sort(
        (a, b) => b.count - a.count || a.name.localeCompare(b.name),
      ),
    });
  });
  return out;
}

/** wbs leaf id → names of ACTIVE drafts whose scope includes it. */
export function draftNamesByWbs(
  drafts: Array<{ name: string; scopeWbsIds: number[] }>,
): Map<number, string[]> {
  const out = new Map<number, string[]>();
  for (const d of drafts) {
    for (const id of d.scopeWbsIds) {
      const names = out.get(id);
      if (names) names.push(d.name);
      else out.set(id, [d.name]);
    }
  }
  return out;
}

/** How a leaf shows in the picker, given the Takt section(s) it's tied to:
 *  - one section → that name, accent chip
 *  - >1 sections → first name + a shared warning, orange chip
 *  - none        → "no section · plan-only", neutral chip
 *  `sections` is the leaf's section-name list (absent/empty ⇒ untied). */
export interface LeafSectionTag {
  kind: "single" | "shared" | "none";
  /** Chip label. */
  label: string;
}

export function leafSectionTag(sections: string[] | undefined): LeafSectionTag {
  const names = sections ?? [];
  if (names.length === 0) return { kind: "none", label: "no section · plan-only" };
  if (names.length === 1) return { kind: "single", label: names[0] };
  return { kind: "shared", label: `${names[0]} ⚠ shared` };
}

/** scope_wbs_ids from an opaque draft payload blob ([] when absent/malformed). */
export function scopeIdsFromPayload(payloadJson: string | null | undefined): number[] {
  if (!payloadJson) return [];
  let data: unknown;
  try {
    data = JSON.parse(payloadJson);
  } catch {
    return [];
  }
  if (typeof data !== "object" || data === null) return [];
  const raw = (data as Record<string, unknown>).scope_wbs_ids;
  if (!Array.isArray(raw)) return [];
  return raw.filter((v): v is number => typeof v === "number");
}
